import { DEFAULT_PAGE_SIZE, ASC, DESC } from "constants/pagination";

const KEY_PAGE = "p";
const KEY_PAGE_SIZE = "s";
const KEY_ORDER_BY = "o";
const KEY_ORDER_DIR = "d";
const KEY_FILTERS = "f";

export const PROCESS_LIST_URL_DEFAULTS = {
  orderBy: "data_criacao",
  orderDirection: DESC,
};

export const TRAFFIC_VIOLATION_LIST_URL_DEFAULTS = {
  orderBy: "data_infracao",
  orderDirection: DESC,
};

export const PERMITTEES_LIST_URL_DEFAULTS = {
  orderBy: "dataCriacao",
  orderDirection: DESC,
};

export const COLLECTIONS_LIST_URL_DEFAULTS = {
  orderBy: "data_criacao",
  orderDirection: DESC,
};

export const PERSON_LIST_URL_DEFAULTS = {
  orderBy: "cpfCnpj",
  orderDirection: DESC,
};

export const PARKING_CREDENTIALS_LIST_URL_DEFAULTS = {
  orderBy: "data_criacao",
  orderDirection: DESC,
};

export const PERMITTEES_DOCUMENTS_LIST_URL_DEFAULTS = {
  orderBy: "data_criacao",
  orderDirection: DESC,
};

export const TRAFFIC_VIOLATION_DOCUMENTS_LIST_URL_DEFAULTS = {
  orderBy: "data_criacao",
  orderDirection: DESC,
};

export const PARKING_CREDENTIALS_DOCUMENTS_LIST_URL_DEFAULTS = {
  orderBy: "data_criacao",
  orderDirection: DESC,
};

export const DRV_LIST_URL_DEFAULTS = {
  orderBy: "data_criacao",
  orderDirection: DESC,
};

/** Default status when no filter is stored in the URL (novos e pendentes). */
export const ALERTS_DEFAULT_LIST_STATUS = "OPENED";

export const ALERTS_LIST_URL_DEFAULTS = {
  orderBy: "numero",
  orderDirection: DESC,
};

function isDefaultPageSize(value) {
  return (value ?? DEFAULT_PAGE_SIZE) === DEFAULT_PAGE_SIZE;
}

/**
 * Syncs list search state (page, page size, sort, filters) to URL query params for restore on return navigation.
 */
export function serializeListStateToUrlParams(state, defaults) {
  const params = new URLSearchParams();
  const page = state.page ?? 1;
  const pageSize = state.pageSize ?? DEFAULT_PAGE_SIZE;
  const orderBy = state.orderBy ?? defaults.orderBy;
  const orderDirection = state.orderDirection ?? defaults.orderDirection;

  if (page !== 1) {
    params.set(KEY_PAGE, String(page));
  }
  if (!isDefaultPageSize(pageSize)) {
    params.set(KEY_PAGE_SIZE, String(pageSize));
  }
  if (orderBy !== defaults.orderBy) {
    params.set(KEY_ORDER_BY, orderBy);
  }
  if (orderDirection !== defaults.orderDirection) {
    params.set(KEY_ORDER_DIR, orderDirection);
  }
  if (filtersHaveActiveValues(state.filters)) {
    params.set(KEY_FILTERS, encodeURIComponent(JSON.stringify(state.filters)));
  }
  return params;
}

export function filtersHaveActiveValues(filters) {
  if (!filters || typeof filters !== "object") return false;
  return Object.values(filters).some((v) => {
    if (v === null || v === undefined) return false;
    if (v === "") return true;
    return String(v).trim() !== "";
  });
}

/**
 * Alerts list: show filter hint when state differs from implicit default (only OPENED, no extra fields).
 * "Todos" (status "") counts as an active filter choice.
 */
export function alertsFiltersShowActiveHint(filters) {
  if (filters == null || typeof filters !== "object") return false;
  const extra = ["numero", "id_usuario_responsavel", "data_inicial", "data_final"].some(
    (key) => {
      const v = filters[key];
      return v != null && String(v).trim() !== "";
    }
  );
  if (extra) return true;
  const st = filters.status;
  if (st === "") return true;
  if (st == null || st === undefined) return false;
  return st !== ALERTS_DEFAULT_LIST_STATUS;
}

/**
 * Reads list state from location.search; merges with defaults for any missing key.
 */
export function parseListStateFromSearch(searchString, defaults) {
  const params = new URLSearchParams(searchString || "");
  const pageRaw = params.get(KEY_PAGE);
  const sizeRaw = params.get(KEY_PAGE_SIZE);
  const orderBy = params.get(KEY_ORDER_BY) || defaults.orderBy;
  const orderDirection = params.get(KEY_ORDER_DIR) || defaults.orderDirection;

  const page = Math.max(1, parseInt(pageRaw, 10) || 1);
  const pageSize = Math.max(1, parseInt(sizeRaw, 10) || DEFAULT_PAGE_SIZE);

  const state = {
    page,
    pageSize,
    orderBy,
    orderDirection,
  };

  const encoded = params.get(KEY_FILTERS);
  if (encoded) {
    try {
      const parsed = JSON.parse(decodeURIComponent(encoded));
      if (parsed && typeof parsed === "object") {
        state.filters = parsed;
      }
    } catch {
      /* ignore invalid JSON */
    }
  }

  return state;
}
